/**
 * ChronicleDetailPage — DEV-1338 (epic DEV-1336).
 *
 * The chronicle detail surface, rebuilt to reuse the SAME chrome as Mini/Full
 * Project detail (PillarPageShell hero + tabs) so chronicles feel like the
 * training ground they are. Mirrors MiniProjectDetailPage's assembly.
 *
 * First cut: About + Media tabs. About reuses ProjectContentSections in
 * DEV-1339; Feed tab lands with DEV-1342; setup moves to ChronicleSettingsModal
 * in DEV-1340 (the gear currently routes to the legacy editor).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PillarPageShell from '../../Components/PillarPage/PillarPageShell';
import { useHeroEdit } from '../../Components/PillarPage/useHeroEdit';
import type { PillarData, TabDefinition } from '../../lib/pillarTypes';
import { fetchChronicle, updateChronicle, type ChronicleResponse } from '../../api/chronicles';
import { submitChronicleForReview } from '../../api/verification';
import ReachModal from '../../Components/ReachModal/ReachModal';
import { fetchOwnerDisplay } from '../../api/ownerDisplay';
import { mediaSrc } from '../../api/mediaUrl';
import FollowButton from '../../Components/FollowButton/FollowButton';
import AttributedChronicleBlocks from '../../Components/AttributedChronicleBlocks/AttributedChronicleBlocks';
import ProjectContentSections from '../../Components/ProjectContentSections/ProjectContentSections';
import ChronicleDetailSections, { type ChronicleSectionKey } from '../../Components/ChronicleSections/ChronicleDetailSections';
import ChronicleDetailSectionsEditor from '../../Components/ChronicleSections/ChronicleDetailSectionsEditor';
import ChronicleLocationBlock from '../../Components/ChronicleSections/ChronicleLocationBlock';
import ChronicleCurrentStatus from '../../Components/ChronicleStatus/ChronicleCurrentStatus';
import ChronicleKeyPlayers from '../../Components/ChronicleKeyPlayers/ChronicleKeyPlayers';
import { getIsSystemAdmin } from '../../auth/AuthContext';
import ChronicleSettingsModal from '../../Components/ChronicleSettingsModal/ChronicleSettingsModal';
import { resolveChronicleType } from '../../Data/Chronicles/chronicleTypes';
import ChronicleSubjectsEditor from '../../Components/ChronicleSubjects/ChronicleSubjectsEditor';
import type { TraySlot } from '../../Components/PillarPage/PillarPageHeader';
import { useAppTranslation } from '../../i18n/useAppTranslation';

interface Props {
  currentUserGuid: string;
  /** Community data arrays (App.tsx) — feed the owner picker in the settings modal. */
  communityEntities?: any[];
  communityRoleAssignments?: any[];
  communityEntityRelationships?: any[];
}

export default function ChronicleDetailPage({
  currentUserGuid,
  communityEntities = [],
  communityRoleAssignments = [],
  communityEntityRelationships = [],
}: Props) {
  const { guid } = useParams<{ guid?: string }>();
  const navigate = useNavigate();
  const { t } = useAppTranslation('pages');

  const [chronicle, setChronicle] = useState<ChronicleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [editing, setEditing] = useState<ChronicleSectionKey | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Reach modal (DEV-1408) — relocated from the inline About-tab ladder. Opened
  // by the header status chip, and once automatically on a successful publish
  // (the "you're live — go further?" moment).
  const [reachOpen, setReachOpen] = useState(false);

  // ── Owner avatar in the hero — resolved exactly like Mini/Full Projects
  //    (user owner → profile photo, community/org owner → emblem cascade). ──
  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<{ kind: 'user' | 'community' | 'unknown'; guid: string; displayName: string } | null>(null);
  const ownerResolvedForKey = useRef<string>('');

  useEffect(() => {
    const tg = chronicle?.ownerGuid;
    if (!tg) return;
    let cancelled = false;
    const entitiesLoaded = (communityEntities ?? []).length > 0;
    const key = `${tg}|${entitiesLoaded ? 'loaded' : 'empty'}`;
    if (ownerResolvedForKey.current === key) return;
    ownerResolvedForKey.current = key;
    fetchOwnerDisplay(tg, communityEntities, communityEntityRelationships)
      .then((display) => {
        if (cancelled) return;
        if (display.avatarUrl) setOwnerAvatarUrl(display.avatarUrl);
        setOwnerInfo({ kind: display.kind, guid: tg, displayName: display.displayName });
      })
      .catch(() => { /* fall back to the initial-letter avatar */ });
    return () => { cancelled = true; };
  }, [chronicle?.ownerGuid, communityEntities, communityEntityRelationships]);

  useEffect(() => {
    if (!guid) return;
    let cancelled = false;
    setLoading(true);
    fetchChronicle(guid)
      .then((c) => {
        if (cancelled) return;
        setChronicle(c);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [guid, reloadKey]);

  // ── Reach state (DEV-1408 / DEV-1409 / DEV-1396) — the single reach summary.
  //    DEV-1396 CORRECTION: reach is DYNAMIC STATE, not descriptive metadata, so
  //    it renders as a STATUS PILL in headerActions (next to "Manage reach"), NOT
  //    in the descriptive tray (DEV-1409 wrongly put it in the tray Status slot;
  //    reversed here). Reflects the HIGHEST reach achieved. Precedence: paused >
  //    reaching > pending > followers > draft. `key` is a stable i18n-free token
  //    used to COLOR the pill; `label` is the localized display string. The
  //    "Reach paused" danger state wins outright — a core edit dropped reach. ──
  const reachState = useMemo<{ key: string; label: string } | null>(() => {
    if (!chronicle) return null;
    const reach = chronicle.reach ?? null;
    const isPublished = chronicle.status === 'published';

    // Paused (danger) — either tier suspended OR the page flag. Takes precedence.
    const suspended =
      chronicle.validationSuspended ||
      reach?.communityFeedStatus === 'suspended' ||
      reach?.searchStatus === 'suspended';
    if (suspended) {
      return { key: 'paused', label: t('chronicleView.reachChip.paused') };
    }

    // Not published → Draft (nothing reaches yet).
    if (!isPublished) {
      return { key: 'draft', label: t('chronicleView.reachChip.draft') };
    }

    // Reaching — search reach is the highest tier; community feed next.
    if (reach?.searchStatus === 'reaching') {
      return { key: 'reachingSearch', label: t('chronicleView.reachChip.reachingSearch') };
    }
    if (reach?.communityFeedStatus === 'reaching') {
      return { key: 'inCommunityFeed', label: t('chronicleView.reachChip.inCommunityFeed') };
    }

    // Pending — anything submitted and awaiting review (community OR search).
    if (reach?.communityFeedStatus === 'pending' || reach?.searchStatus === 'pending') {
      return { key: 'inReview', label: t('chronicleView.reachChip.inReview') };
    }

    // Published, nothing submitted → Followers (the existence floor).
    return { key: 'followers', label: t('chronicleView.reachChip.followers') };
  }, [chronicle, t]);

  // Localized chronicle content-type label for the tray "Type" slot (DEV-1396).
  const typeLabel = useMemo<string>(() => {
    if (!chronicle) return '';
    const def = resolveChronicleType(chronicle.categoryKey || '');
    return def ? t(def.labelKey) : (chronicle.categoryKey || '');
  }, [chronicle, t]);

  // ── PillarData (the shape the shared header consumes). DEV-1396: reach is no
  //    longer fed into status/statusKey — it's a header pill now (see below). The
  //    descriptive tray slots are supplied separately via `traySlots`. ──
  const pillarData: PillarData | null = useMemo(() => {
    if (!chronicle) return null;
    return {
      guid: chronicle.chronicleGuid,
      name: chronicle.title,
      description: chronicle.subtitle || undefined,
      heroImageUrl: chronicle.heroImageUrl || undefined,
      followersCount: 0,
      contributorsCount: 0,
      sdgBadges: [],
      ownerKind: ownerInfo?.kind && ownerInfo.kind !== 'unknown'
        ? ownerInfo.kind
        : (chronicle.ownerType === 'community' ? 'community' : 'user'),
      ownerGuid: ownerInfo?.guid || chronicle.ownerGuid || undefined,
      ownerDisplayName: ownerInfo?.displayName || chronicle.ownerDisplayName || undefined,
      ownerAvatarUrl: ownerAvatarUrl || undefined,
      relationships: {
        problems_count: 0,
        solutions_count: 0,
        enablers_count: 0,
        communities_count: 0,
        projects_count: 0,
      },
    };
  }, [chronicle, ownerInfo, ownerAvatarUrl]);

  const canEdit = useMemo(() => {
    if (!chronicle) return false;
    const me = (currentUserGuid || '').toUpperCase();
    return (chronicle.createdByGuid || '').toUpperCase() === me
      || (chronicle.initiatorGuid || '').toUpperCase() === me
      || getIsSystemAdmin();
  }, [chronicle, currentUserGuid]);

  // ── Descriptive tray slots (DEV-1396) — Type (content kind) + Subjects (tags).
  //    Replaces the project Category/Status/Timeline/Content slots for chronicles.
  //    Reach is NOT here (it's a status pill — dynamic state, not metadata). The
  //    Subjects slot value is a node: chips + an author-only editor. ──
  const traySlots: TraySlot[] = useMemo(() => {
    if (!chronicle) return [];
    return [
      { label: t('chronicleView.tray.type'), value: typeLabel },
      {
        label: t('chronicleView.tray.subjects'),
        node: (
          <ChronicleSubjectsEditor
            chronicleGuid={chronicle.chronicleGuid}
            subjects={chronicle.subjects ?? []}
            canEdit={canEdit}
            onChanged={() => setReloadKey((k) => k + 1)}
          />
        ),
      },
    ];
  }, [chronicle, t, typeLabel, canEdit]);

  // Has the content declared at least one subject? Gates the expert-review submit
  // (DEV-1396) — subjects route content to matching reviewers, so ≥1 is required.
  const hasSubjects = (chronicle?.subjects?.length ?? 0) > 0;

  // Color token for the reach status pill, keyed off the stable reachState.key.
  const reachPillColor = useMemo<string>(() => {
    switch (reachState?.key) {
      case 'paused': return 'var(--color-error)';
      case 'reachingSearch':
      case 'inCommunityFeed': return 'var(--color-success)';
      case 'inReview': return 'var(--color-warning)';
      case 'followers': return 'var(--info-text)';
      case 'draft':
      default: return 'var(--text-muted)';
    }
  }, [reachState]);

  // ── Cover photo editing — the SAME shared control the projects use ──
  const persistChronicleHero = useCallback((url: string) => {
    if (!chronicle) return;
    updateChronicle(chronicle.chronicleGuid, { heroImageUrl: url })
      .then(() => setReloadKey((k) => k + 1))
      .catch((err) => window.alert('Cover photo update failed: ' + (err instanceof Error ? err.message : String(err))));
  }, [chronicle]);
  const removeChronicleHero = useCallback(() => {
    if (!chronicle) return;
    updateChronicle(chronicle.chronicleGuid, { heroImageUrl: '' })
      .then(() => setReloadKey((k) => k + 1))
      .catch((err) => window.alert('Cover photo remove failed: ' + (err instanceof Error ? err.message : String(err))));
  }, [chronicle]);
  // ── Publish / Move-to-draft — DEV-1409 relocates this into the ReachModal's
  //    "Visibility" zone (above the ladder). The header no longer toggles state;
  //    publishing now happens inside the modal, so the modal simply stays open
  //    after a toggle (no jarring close/reopen). draft ⇄ published via the same
  //    status field; author/sysadmin only. Lifted to the parent so the modal can
  //    call it while ChronicleDetailPage owns the chronicle + reload. ──
  const handleTogglePublish = useCallback(() => {
    if (!chronicle) return;
    const next = chronicle.status === 'published' ? 'draft' : 'published';
    setPublishing(true);
    updateChronicle(chronicle.chronicleGuid, { status: next })
      .then(() => {
        setReloadKey((k) => k + 1);
      })
      .catch((err) => window.alert('Publish update failed: ' + (err instanceof Error ? err.message : String(err))))
      .finally(() => setPublishing(false));
  }, [chronicle]);

  // ── Submit for review — the POOL-submission front door (DEV-1398). Publish
  //    controls *existence* (own page + followers); submit controls *reach*
  //    (community feeds + search). Orthogonal — SPEC §12.1. draft|changes_requested
  //    → submitted; author/sysadmin only; hidden for 'light' content (self-publishes). ──
  const handleSubmitForReview = useCallback(() => {
    if (!chronicle) return;
    // Gate-2 requires ≥1 subject (DEV-1396). The ladder disables the button when
    // there are none; this guard + the 400-aware error string handle it defensively.
    if ((chronicle.subjects?.length ?? 0) === 0) {
      window.alert(t('chronicleView.subjects.submitHint'));
      return;
    }
    setSubmitting(true);
    submitChronicleForReview(chronicle.chronicleGuid)
      .then(() => {
        window.alert(t('chronicleView.submitForReviewSuccess'));
        setReloadKey((k) => k + 1);
      })
      .catch((err) => {
        // The server 400s when no subject is set — surface the actionable hint.
        const msg = String(err?.message || '').toLowerCase();
        if (msg.includes('subject')) {
          window.alert(t('chronicleView.subjects.submitHint'));
        } else {
          window.alert(t('chronicleView.submitForReviewError'));
        }
      })
      .finally(() => setSubmitting(false));
  }, [chronicle, t]);

  const { menu: heroEditControls, overlay: heroCropOverlay, menuOpen: heroMenuOpen } = useHeroEdit({
    canEdit,
    currentHeroUrl: chronicle?.heroImageUrl ?? null,
    uploadBucket: 'chronicle-hero',
    uploadEntityKey: chronicle?.chronicleGuid,
    onPersist: persistChronicleHero,
    onRemove: removeChronicleHero,
  });

  // ── Tabs (About / Media — mirrors the project tab pattern) ──
  const tabs: TabDefinition[] = useMemo(() => {
    if (!chronicle) return [];
    // Match the project About width — fill the shared shell area (no narrow cap).
    const colStyle: React.CSSProperties = {
      marginInline: 'auto',
      width: '100%',
      paddingBlock: 16,
      paddingInline: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    };
    const editBtnStyle: React.CSSProperties = { padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600, fontSize: 13 };
    // Per-section read/edit block — each section (and tab) edits only itself.
    const sectionBlock = (key: ChronicleSectionKey) =>
      editing === key && canEdit ? (
        <ChronicleDetailSectionsEditor
          chronicle={chronicle}
          include={[key]}
          onSaved={() => { setEditing(null); setReloadKey((k) => k + 1); }}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <>
          <ChronicleDetailSections chronicle={chronicle} include={[key]} />
          {canEdit && (
            <div>
              <button type="button" onClick={() => setEditing(key)} style={editBtnStyle}>{t('chronicleView.editDetails')}</button>
            </div>
          )}
        </>
      );
    return [
      {
        key: 'about',
        label: t('chronicleView.tabAbout'),
        content: (
          <div style={colStyle}>
            {/* Reach controls relocated to the ReachModal (DEV-1408), opened by the
                header status chip + on publish. The About tab is content-only now —
                a distribution control no longer sits inline in descriptive content. */}
            {/* Short description (subtitle) — leads the About tab, like projects. */}
            {chronicle.subtitle && (
              <div style={{ fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 500, borderInlineStart: '3px solid var(--accent-primary)', paddingInlineStart: 12 }}>
                {chronicle.subtitle}
              </div>
            )}
            {chronicle.sourceType === 'attributed' && <AttributedChronicleBlocks chronicle={chronicle} />}
            {/* Current Status — pinned now-state, above Overview (DEV-1352). */}
            <ChronicleCurrentStatus chronicleGuid={chronicle.chronicleGuid} canEdit={canEdit} />
            {/* Overview + body via the shared ProjectContentSections engine (DEV-1339). */}
            <ProjectContentSections entityType="chronicle" entityGuid={chronicle.chronicleGuid} canEdit={canEdit} isEditing={canEdit} slot="overview" />
            {/* Where it happened: pinned-location map + local conditions (note 6). */}
            <ChronicleLocationBlock chronicle={chronicle} />
            {/* Key Players / Leading Characters — per-card add/edit tray (DEV-1352). */}
            <ChronicleKeyPlayers chronicle={chronicle} canEdit={canEdit} onChanged={() => setReloadKey((k) => k + 1)} />
            {/* "+ Add Section" lives in the body slot — below Key Players (note 3). */}
            <ProjectContentSections entityType="chronicle" entityGuid={chronicle.chronicleGuid} canEdit={canEdit} isEditing={canEdit} slot="body" />
            {sectionBlock('links')}
            {chronicle.missionNarrative && (
              <section>
                <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {chronicle.missionNarrative}
                </div>
              </section>
            )}
            {chronicle.background && (
              <section>
                <div style={{ color: 'var(--text-tertiary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {chronicle.background}
                </div>
              </section>
            )}
          </div>
        ),
      },
      {
        key: 'timeline',
        label: t('chronicleView.tabTimeline'),
        content: <div style={colStyle}>{sectionBlock('timeline')}</div>,
      },
      {
        key: 'news',
        label: t('chronicleView.tabNews'),
        content: <div style={colStyle}>{sectionBlock('news')}</div>,
      },
      // Impact + Media are Project-Chronicle only; Topic Trackers omit them (DEV-1352).
      ...(chronicle.categoryKey === 'topic_tracker' ? [] : [
      {
        key: 'impact',
        label: t('chronicleView.tabImpact'),
        content: <div style={colStyle}>{sectionBlock('outcomes')}</div>,
      },
      {
        key: 'media',
        label: t('chronicleView.tabMedia'),
        content: (
          <div style={{ width: '100%', marginInline: 'auto', paddingBlock: 16, paddingInline: 16 }}>
            {(chronicle.media ?? []).length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {chronicle.media.map((m) => (
                  <div
                    key={m.mediaGuid}
                    style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)' }}
                  >
                    <img
                      src={mediaSrc(m.fileUrl) ?? undefined}
                      alt={m.caption ?? ''}
                      style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                    />
                    {m.caption && (
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '6px 10px' }}>{m.caption}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', padding: 24 }}>{t('chronicleView.mediaEmpty')}</div>
            )}
          </div>
        ),
      },
      ]),
    ];
  }, [chronicle, t, canEdit, editing]);

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>{t('chronicleView.loading')}</div>;
  }
  if (notFound || !chronicle || !pillarData) {
    return <div style={{ padding: 24, color: 'var(--text-secondary)' }}>{t('chronicleView.notFound')}</div>;
  }

  return (
    <>
    <PillarPageShell
      pillarType="projects"
      data={pillarData}
      traySlots={traySlots}
      tabs={tabs}
      activeTabIndex={activeTabIndex}
      onTabChange={setActiveTabIndex}
      heroImageUrl={chronicle.heroImageUrl || undefined}
      heroEditControls={heroEditControls}
      heroCropOverlay={heroCropOverlay}
      heroMenuOpen={heroMenuOpen}
      isHeroExpanded
      onEdit={() => setShowSettings(true)}
      onClose={() => navigate('/content')}
      headerActions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Reach status PILL (DEV-1396) — descriptive, read-only dynamic state,
              restored next to "Manage reach". This is the reach SUMMARY (the
              DEV-1409 tray Status slot is reversed — reach is state, not metadata).
              The "Manage reach" button below stays the OPENER. */}
          {reachState && (
            <span
              style={{
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
                color: reachPillColor,
                border: `1px solid ${reachPillColor}`,
                background: 'rgba(0,0,0,0.25)',
              }}
            >
              {reachState.label}
            </span>
          )}
          {/* Manage reach (DEV-1409) — the ONE acting control in the header, and
              all it does is OPEN the ReachModal. Its label NEVER changes with
              state (the old chip+Publish pair flipped its label as the dialog
              opened, leaving you unsure whether you'd unpublished). Both the
              current reach STATE (read-only) and the Publish / Move-to-draft and
              reach ACTIONS now live inside the modal. Author/sysadmin only. */}
          {canEdit && (
            <button
              type="button"
              onClick={() => setReachOpen(true)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                cursor: 'pointer',
                border: '1px solid var(--border-default)',
                background: 'transparent',
                color: 'var(--text-secondary)',
              }}
            >
              {t('chronicleView.manageReach')}
            </button>
          )}
          <FollowButton targetType="chronicle" targetGuid={chronicle.chronicleGuid} showCount={false} compact />
        </div>
      }
    />
    {reachOpen && (
      <ReachModal
        chronicleGuid={chronicle.chronicleGuid}
        isPublished={chronicle.status === 'published'}
        reach={chronicle.reach}
        onTogglePublish={handleTogglePublish}
        togglingPublish={publishing}
        onSubmitForExpertReview={handleSubmitForReview}
        submittingExpert={submitting}
        canSubmitForExpertReview={chronicle.reviewModel !== 'light'}
        hasSubjects={hasSubjects}
        onCommunitySubmitted={() => setReloadKey((k) => k + 1)}
        onClose={() => setReachOpen(false)}
      />
    )}
    {showSettings && (
      <ChronicleSettingsModal
        chronicle={chronicle}
        currentUserGuid={currentUserGuid}
        communityEntities={communityEntities}
        communityRoleAssignments={communityRoleAssignments}
        communityEntityRelationships={communityEntityRelationships}
        onClose={() => setShowSettings(false)}
        onSaved={() => setReloadKey((k) => k + 1)}
        onDeleted={() => navigate('/content')}
      />
    )}
    </>
  );
}
